import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Equipment,
  EquipmentInput,
  ListEquipmentsResponse,
  ListDigitalCentersResponse,
} from 'shared';

export type { Equipment, EquipmentInput };

// ─── Query key factory ────────────────────────────────────────────────────────

export const eqKeys = {
  all: ['equipments'] as const,
  list: (params: ListParams) => [...eqKeys.all, 'list', params] as const,
  detail: (id: number) => [...eqKeys.all, 'detail', id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ListParams {
  limit?: number;
  offset?: number;
  search?: string;
  digitalCenterID?: number;
  estado?: string;
  showDeleted?: boolean;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Paginated, searchable and filterable list of equipments. */
export function useEquipments(params: ListParams = {}) {
  const {
    limit = 20,
    offset = 0,
    search = '',
    digitalCenterID,
    estado = '',
    showDeleted = false,
  } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(search ? { search } : {}),
    ...(digitalCenterID ? { digitalCenterID: String(digitalCenterID) } : {}),
    ...(estado ? { estado } : {}),
    ...(showDeleted ? { showDeleted: 'true' } : {}),
  });

  return useQuery({
    queryKey: eqKeys.list(params),
    queryFn: () => api.get<ListEquipmentsResponse>(`/api/equipments?${qs}`),
    staleTime: 0,
    placeholderData: (prev) => prev,
  });
}

/** Single equipment by ID. */
export function useEquipment(id: number | null, showDeleted = false) {
  return useQuery({
    queryKey: eqKeys.detail(id!),
    queryFn: () =>
      api.get<Equipment>(
        `/api/equipments/${id}${showDeleted ? '?showDeleted=true' : ''}`,
      ),
    enabled: id !== null,
  });
}

/** Create a new equipment. */
export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EquipmentInput) =>
      api.post<Equipment>('/api/equipments', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: eqKeys.all }),
  });
}

/** Update an existing equipment. */
export function useUpdateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EquipmentInput }) =>
      api.put<Equipment>(`/api/equipments/${id}`, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: eqKeys.all });
      qc.invalidateQueries({ queryKey: eqKeys.detail(id) });
    },
  });
}

/** Soft-delete an equipment (requires a reason). */
export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.delete<void>(`/api/equipments/${id}`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eqKeys.all }),
  });
}

/** Active (non-deleted) digital centres for use in dropdowns. */
export function useDigitalCentersDropdown() {
  return useQuery({
    queryKey: ['digital-centers', 'dropdown'],
    queryFn: () =>
      api.get<ListDigitalCentersResponse>('/api/digital-centers?limit=100&offset=0'),
    staleTime: 1000 * 60 * 5,
    select: (data) => data.digitalCenters,
  });
}
